import { useEffect, useRef, useState } from 'react';

import {
  Alert,
  Anchor,
  Box,
  Checkbox,
  Icon,
  Label,
  LabeledControl,
  Paragraph,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import { IconCheckmarkCircleFilled } from '@zillow/constellation-icons';

import { useRentalManagerUserPropertyDigitallyAlteredPhotosWebApiHandler_attest } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { EXTERNAL_URLS } from '@zg-rentals/zillow-rental-manager/constants';

import { AI_IMAGE_LISTING_COMPLIANCE_DETAILS_TAG } from '../../../../hooks/queries/useAiImageListingCompliance';

export const DIGITALLY_ALTERED_PHOTOS_ATTESTATION_CONTROL_ID = 'digitally-altered-photos-inline-attestation';

type Props = {
  listingAlias: string;
  isAttestationCompleted: boolean;
  hasPhotos: boolean;
  onReviewPhotos: () => void;
};

const DigitallyAlteredPhotosAlert = ({ listingAlias, isAttestationCompleted, hasPhotos, onReviewPhotos }: Props) => {
  const { enqueueToast } = useToast();
  const { mutateAsync: attest } = useRentalManagerUserPropertyDigitallyAlteredPhotosWebApiHandler_attest({
    meta: {
      invalidates: [AI_IMAGE_LISTING_COMPLIANCE_DETAILS_TAG],
      context: { api: 'digitallyAlteredPhotosAttest', details: { listingAlias } },
    },
  });

  const confirmationRef = useRef<HTMLParagraphElement>(null);
  const [hasAttestedHere, setHasAttestedHere] = useState(false);

  useEffect(() => {
    if (isAttestationCompleted && hasAttestedHere) {
      confirmationRef.current?.focus();
    }
  }, [isAttestationCompleted, hasAttestedHere]);

  return (
    <Alert
      data-testid="DigitallyAlteredPhotosAlert"
      tone={isAttestationCompleted ? 'info' : 'warning'}
      css={{ marginBottom: '400' }}
    >
      <Paragraph>
        <Anchor href={EXTERNAL_URLS.CA_AB_723} target="_blank" rel="noopener noreferrer">
          California AB 723
        </Anchor>{' '}
        requires real-estate sales ads to label digitally altered photos and give viewers access to the original images.
        Review each photo and mark any digitally altered images.
      </Paragraph>
      <Paragraph css={{ marginTop: '300' }}>
        <strong>What counts:</strong> Adding, removing, or changing furniture, property features, or views — for
        example, virtually staging a room, removing a wall, or changing the view outside a window.
      </Paragraph>
      <Paragraph css={{ marginTop: '300' }}>
        <strong>What doesn’t:</strong> Routine lighting adjustments or cropping, as long as they don’t change how the
        property is represented.
      </Paragraph>
      {isAttestationCompleted ? (
        hasAttestedHere && (
          <Box css={{ display: 'flex', alignItems: 'center', gap: 'default', flexWrap: 'wrap', marginTop: '400' }}>
            <Paragraph
              ref={confirmationRef}
              role="status"
              tabIndex={-1}
              css={{ display: 'flex', alignItems: 'center', gap: 'tight', outline: 'none' }}
            >
              <Icon size="sm" css={{ color: 'icon.feedback.success.hero' }} render={<IconCheckmarkCircleFilled />} />
              {hasPhotos
                ? 'Photo review confirmed'
                : 'Photo review confirmed. Mark any digitally altered photos after you add them.'}
            </Paragraph>
            {hasPhotos && <TextButton onClick={onReviewPhotos}>Review photos</TextButton>}
          </Box>
        )
      ) : (
        <LabeledControl
          css={{ marginTop: '400' }}
          controlId={DIGITALLY_ALTERED_PHOTOS_ATTESTATION_CONTROL_ID}
          label={<Label>I will mark photos that have been digitally altered</Label>}
          control={
            <Checkbox
              checked={hasAttestedHere}
              aria-disabled={hasAttestedHere}
              onChange={async () => {
                if (hasAttestedHere) {
                  return;
                }
                setHasAttestedHere(true);
                try {
                  await attest({ postData: { listingAlias } });
                } catch {
                  setHasAttestedHere(false);
                  enqueueToast(
                    <Toast tone="critical">Your acknowledgment couldn’t be saved. Refresh and try again.</Toast>,
                  );
                }
              }}
            />
          }
        />
      )}
    </Alert>
  );
};

export default DigitallyAlteredPhotosAlert;
